/**
 * Advances Table Component
 * Displays advance payment transactions
 * Filters for transactions where feeType contains "Advance"
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';

export class AdvancesTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            flattenedData: [],
            isLoading: false
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind methods
        this.syncScroll = this.syncScroll.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    componentDidMount() {
        console.log('AdvancesTable component mounted');
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    processTransactionsData(data) {
        console.log('AdvancesTable - Processing transactions data:', data);
        
        const collections = data?.collections || [];

        if (collections && collections.length > 0) {
            // Filter for advance transactions
            const advanceTransactions = [];

            collections.forEach(transaction => {
                const feePaidDetails = transaction.feePaidDetails;

                if (feePaidDetails && Array.isArray(feePaidDetails)) {
                    // Check if any feePaidDetail has feeType containing "Advance"
                    const hasAdvance = feePaidDetails.some(detail => 
                        detail.feeType && detail.feeType.toLowerCase().includes('advance')
                    );

                    if (hasAdvance) {
                        advanceTransactions.push(transaction);
                    }
                }
            });

            console.log('Filtered advance transactions:', advanceTransactions.length);

            const transformedData = this.transformApiDataToTableFormat(advanceTransactions);
            console.log('AdvancesTable - Transformed Data:', transformedData);
            
            const flattened = this.flattenTransactions(transformedData);
            console.log('AdvancesTable - Flattened Data:', flattened);
            
            this.setState({
                transactions: transformedData,
                flattenedData: flattened,
                isLoading: false
            }, () => {
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.log('AdvancesTable - No collections in data');
            this.setState({ 
                transactions: [],
                flattenedData: [],
                isLoading: false 
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentWillUnmount() {
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    attachScrollListener = () => {
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
            grandTotalWrapper.addEventListener('scroll', this.syncScroll);
            console.log('Scroll listener attached to grand total');
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.scrollLeft = scrollLeft;
        }
    }

    flattenTransactions = (transactionGroups) => {
        const flattened = [];
        let globalSNo = 1;

        transactionGroups.forEach((group, groupIndex) => {
            const feeDetails = group.feeDetails || [];
            const totalRows = feeDetails.length;

            feeDetails.forEach((feeDetail, feeIndex) => {
                flattened.push({
                    sNo: globalSNo,
                    feeDetail: feeDetail,
                    groupIndex: groupIndex,
                    feeIndex: feeIndex,
                    isFirstRow: feeIndex === 0,
                    isLastRow: feeIndex === totalRows - 1,
                    totalRowSpan: totalRows,
                    transactionId: feeDetail.transactionId,
                    receiptNo: feeDetail.receiptNo,
                    uniqueKey: `${globalSNo}-${feeDetail.receiptNo}-${feeIndex}`
                });
            });

            globalSNo++;
        });

        return flattened;
    };

    transformApiDataToTableFormat = (collections) => {
        if (!collections || collections.length === 0) {
            return [];
        }

        const result = [];
        let sNoCounter = 1;

        collections.forEach((collection) => {
            const transactionId = collection.transctionId || '';
            const receiptNo = collection.receiptNo || 'N/A';
            const receiptDate = collection.receiptDate || null;
            const transactionDate = collection.transactionDate || null;
            const paymentMode = collection.paymentMode || 'N/A';
            const remarks = collection.remarks || null;
            const totalAmountPaid = collection.amountPaid || 0;

            const transactionFeeDetails = [];

            // Each feePaidDetails item becomes a row in the table (only advances)
            if (collection.feePaidDetails && collection.feePaidDetails.length > 0) {
                collection.feePaidDetails.forEach((feeDetail) => {
                    // Only include advance fees
                    if (feeDetail.feeType && feeDetail.feeType.toLowerCase().includes('advance')) {
                        // Handle fee type display - don't show "undefined" or empty payment term
                        let feeTypeDisplay = feeDetail.feeType || 'N/A';
                        if (feeDetail.paymentTerm && feeDetail.paymentTerm !== 'undefined') {
                            feeTypeDisplay = `${feeTypeDisplay} - ${feeDetail.paymentTerm}`;
                        }

                        transactionFeeDetails.push({
                            transactionId: transactionId,
                            remarks: remarks,
                            receiptNo: receiptNo,
                            receiptDate: receiptDate,
                            transactionDate: transactionDate,
                            paymentMode: paymentMode,
                            feeType: feeTypeDisplay,
                            paidAmount: feeDetail.amountPaying || 0,
                            total: totalAmountPaid
                        });
                    }
                });
            }

            // Only add if there are advance fee details
            if (transactionFeeDetails.length > 0) {
                result.push({
                    sNo: sNoCounter++,
                    transactionId: transactionId,
                    receiptNo: receiptNo,
                    feeDetails: transactionFeeDetails
                });
            }
        });

        console.log('Advances Groups:', result);
        return result;
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    calculateGrandTotal = () => {
        const { flattenedData } = this.state;
        return flattenedData.reduce((sum, row) => sum + (row.feeDetail.paidAmount || 0), 0);
    };

    // Column body templates
    snoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    };

    transactionIdBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        const txnId = rowData.feeDetail.transactionId || 'N/A';
        return (
            <div className="transaction-id-info">
                <div className="transaction-id">{txnId}</div>
                {rowData.feeDetail.remarks && (
                    <div className="remarks">Remarks : {rowData.feeDetail.remarks}</div>
                )}
            </div>
        );
    };

    receiptNoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail.receiptNo}</span>;
    };

    receiptDateBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{this.formatDate(rowData.feeDetail.receiptDate)}</span>;
    };

    paymentModeBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail.paymentMode}</span>;
    };

    feeTypeBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.feeType}</span>;
    };

    paidAmountBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.paidAmount.toFixed(2)}</span>;
    };

    totalBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail.total.toFixed(2)}</span>;
    };

    rowClassName = (rowData) => {
        const classes = [];

        if (rowData.isFirstRow) {
            classes.push('transaction-start-row');
        }

        if (rowData.isLastRow) {
            classes.push('transaction-last-row');
        }

        return classes.join(' ');
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        return (
            <div className="advances-table-container">
                {isLoading ? (
                    <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading transactions...</p>
                    </div>
                ) : (
                    <div className="payment-mode-table-wrapper">
                        <div ref={this.dataScrollRef} className="data-table-scroll-container">
                            <DataTable
                                value={flattenedData}
                                scrollable
                                scrollHeight="flex"
                                className="payment-mode-table"
                                showGridlines
                                rowClassName={this.rowClassName}
                                emptyMessage="No advance transactions found"
                            >
                                <Column
                                    header="S.No"
                                    body={this.snoBodyTemplate}
                                    style={{ width: '80px', minWidth: '80px' }}
                                />
                                <Column
                                    header="TransactionID"
                                    body={this.transactionIdBodyTemplate}
                                    style={{ width: '250px', minWidth: '250px' }}
                                />
                                <Column
                                    header="Receipt No"
                                    body={this.receiptNoBodyTemplate}
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Receipt Date"
                                    body={this.receiptDateBodyTemplate}
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Payment Mode"
                                    body={this.paymentModeBodyTemplate}
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Fee Type"
                                    body={this.feeTypeBodyTemplate}
                                    style={{ width: '280px', minWidth: '280px' }}
                                />
                                <Column
                                    header="Paid Amount"
                                    body={this.paidAmountBodyTemplate}
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Total"
                                    body={this.totalBodyTemplate}
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                            </DataTable>
                        </div>

                        {/* Grand Total Row */}
                        <div ref={this.grandTotalScrollRef}>
                            <DataTable
                                value={[{ total: grandTotal }]}
                                className="payment-mode-grand-total-table"
                                scrollable
                                scrollHeight="flex"
                                showGridlines
                            >
                                <Column body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                <Column body={() => ''} style={{ width: '250px', minWidth: '250px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '280px', minWidth: '280px' }} />
                                <Column body={() => <strong style={{ color: '#076EFF' }}>Total</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'right', paddingRight: '1rem' }} />
                                <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.toFixed(2)}</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} />
                            </DataTable>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
